import { FC } from 'react';
import {
  ActionsMenu,
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  TruncatedText,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { Divider, Stack } from '@mui/material';
import { APP, ROUTES } from 'config';
import {
  AdminDiscountsFilter,
  DateRangeFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import moment from 'moment';
import {
  COLOR_BY_STATUS,
  feedbackFiltersSchema,
  feedbackStatuses,
  useBulkDeleteFeedback,
  useBulkUpdateFeedbackStatus,
  useDiscountFeedbackItems,
  useExportXlsDiscountFeedbackItems,
} from 'store/feedback';
import { useSearchParams } from 'utils/navigation';
import { useConfirm } from 'store/confirm';
import { usePredefinedToasts } from 'utils/Toast';
import { DownloadXlsAction } from 'components/actions';

export const AdminDiscountReportsList: FC = () => {
  const { t } = useTranslation();
  const { confirmation } = useConfirm();
  const predefinedToasts = usePredefinedToasts();
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();

  const { searchParams: filters, navigate } = useSearchParams(
    feedbackFiltersSchema,
  );

  const { data: feedbackItems, isLoading: isFeedbackLoading } =
    useDiscountFeedbackItems(filters);

  const { mutate: updateStatus, isLoading: isUpdatingStatus } =
    useBulkUpdateFeedbackStatus();
  const { mutate: deleteFeedback, isLoading: isDeleting } =
    useBulkDeleteFeedback();
  const { mutate: exportToXls, isLoading: isExportingToXls } =
    useExportXlsDiscountFeedbackItems();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <ListTotal total={feedbackItems?.total} />
          <Stack direction="row" alignItems="center" gap={1}>
            <Filters
              value={filters}
              initialValues={feedbackFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: { ...v, page: 1 } })}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  <FilterButton
                    label={t('marketplaceItems.discountPercentage')}
                    value={value.entityIds}
                    initialValue={initialValues.entityIds}
                    onChange={(v) => setFilter('entityIds', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <AdminDiscountsFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('common.status')}
                    value={value.status}
                    initialValue={initialValues.status}
                    onChange={(v) => setFilter('status', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        name="status"
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        searchable={false}
                        options={feedbackStatuses.map((status) => ({
                          key: status,
                          label: t(`status.${status}`),
                        }))}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('common.date')}
                    value={value.date}
                    initialValue={initialValues.date}
                    onChange={(v) => setFilter('date', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <DateRangeFilter
                        value={value}
                        onApply={applyFilter}
                        onClear={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <OpenSearchFilter
                    value={value.search}
                    onApplyFilter={(v) => setFilter('search', v)}
                    placeholder={t('common.search')}
                  />
                </>
              )}
            </Filters>

            <Divider orientation="vertical" variant="middle" flexItem />

            <DownloadXlsAction
              listLength={feedbackItems?.total ?? 0}
              exportToExcel={() => exportToXls({ filters })}
              loading={isExportingToXls}
            />
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={feedbackItems?.items ?? []}
        isLoading={isFeedbackLoading}
        columns={[
          {
            id: 'name',
            header: t('marketplaceItems.discountPercentage'),
            cell: ({ row: { original: feedback } }) => (
              <Link
                label={feedback.entity.label}
                to={ROUTES.admin.discounts.details.settings.replace(
                  ':id',
                  feedback.entity.id,
                )}
              />
            ),
          },
          {
            id: 'date',
            header: t('common.date'),
            cell: ({ row: { original: feedback } }) =>
              moment(feedback.createdAt).format('l'),
            meta: { sort: true },
          },
          {
            id: 'comment',
            header: t('clients.comment'),
            cell: ({ row: { original: feedback } }) => (
              <TruncatedText
                text={feedback.content}
                maxLength={300}
                textProps={{ color: 'inherit' }}
              />
            ),
          },
          {
            id: 'status',
            header: t('common.status'),
            cell: ({ row: { original: feedback } }) => (
              <Typography color={COLOR_BY_STATUS[feedback.status]}>
                {t(`status.${feedback.status}`)}
              </Typography>
            ),
            meta: { sort: true },
          },
          {
            id: 'actions',
            cell: ({ row: { original: feedback } }) => {
              return (
                <ActionsMenu
                  type="compact"
                  items={[
                    ...feedbackStatuses.map((status) => ({
                      label: t('global.actions.mark_as', {
                        status: t(`status.${status}`),
                      }),
                      onClick: () => {
                        updateStatus(
                          {
                            ids: [feedback.id],
                            status,
                          },
                          {
                            onSuccess: () => {
                              predefinedToasts.success.updated();
                            },
                          },
                        );
                      },
                    })),
                    {
                      label: t('actions.delete'),
                      onClick: async () => {
                        const { confirmed } = await confirmation(
                          t('common.areYouSureToDelete', {
                            title: feedback.id,
                          }),
                          undefined,
                          'danger',
                        );

                        if (confirmed) {
                          deleteFeedback(
                            { ids: [feedback.id] },
                            {
                              onSuccess: () => {
                                predefinedToasts.success.deleted();
                              },
                            },
                          );
                        }
                      },
                      color: 'error',
                      disabled: isDeleting,
                    },
                  ]}
                />
              );
            },
          },
        ]}
        selection={{
          value: selectedRowIds,
          onChange: setSelectedRowIds,
          isLoading: isUpdatingStatus || isDeleting,
          actions: [
            ...feedbackStatuses.map((status) => ({
              label: t('global.actions.mark_as', {
                status: t(`status.${status}`),
              }),
              onClick: () => {
                updateStatus(
                  {
                    ids: Object.keys(selectedRowIds),
                    status,
                  },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                      setSelectedRowIds({});
                    },
                  },
                );
              },
            })),
            {
              label: t('actions.delete'),
              onClick: async () => {
                const { confirmed } = await confirmation(
                  undefined,
                  undefined,
                  'danger',
                );

                if (confirmed) {
                  deleteFeedback(
                    { ids: Object.keys(selectedRowIds) },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.deleted();
                        setSelectedRowIds({});
                      },
                    },
                  );
                }
              },
              color: 'error',
              disabled: isDeleting,
            },
          ],
        }}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        pagination={
          feedbackItems && {
            count: feedbackItems.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
